import {
  UseLazyQuery,
  UseQuery
} from "@reduxjs/toolkit/dist/query/react/buildHooks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { QueryDefinition } from "@reduxjs/toolkit/query";
import { isEqual } from "lodash";

import { useAppDispatch } from "app/hooks";
import { emptySplitApi } from "services/empty-split-api";
import { safeRefetch } from "utils/services.util";

const DEFAULT_LIMIT = 5;
type QDef<QueryArg, ResultType> = QueryDefinition<
  QueryArg & { offset?: number; limit?: number },
  any,
  string,
  ResultType,
  string
>;

type UsePaginationProps<QueryArg> = {
  endpointName: string;
  params: QueryArg;
  limit?: number;
  options?: any;
  withTransition?: boolean;
  showCurrentData?: boolean;
  /**
   * Show a row whose `id` is already on an earlier page only once. Opt in only where every
   * row's `id` is unique across the list: some endpoints union two tables (`/payments/`
   * returns Payment and Refund rows), where equal ids are different rows.
   */
  uniqueById?: boolean;
};

export default function usePaginationV2<QueryArg, ResultType>({
  endpointName,
  limit = DEFAULT_LIMIT,
  params,
  options,
  withTransition,
  showCurrentData,
  uniqueById = false
}: UsePaginationProps<QueryArg>) {
  const [newOffset, setNewOffset] = useState<number | undefined>(undefined);
  const [offset, setOffset] = useState(0);
  const [hasMoreData, setHasMoreData] = useState<boolean>(true);
  const [currentParams, setCurrentParams] = useState<QueryArg>(params);
  const [currentOptions, setCurrentOptions] = useState<any>(options);
  const [isDataPending, setIsDataPending] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  // The params whose cache entry is on screen; read by clearData, which must stay stable.
  const currentParamsRef = useRef<QueryArg>(params);
  const dispatch = useAppDispatch();

  const { useQuery, useLazyQuery, select } = emptySplitApi.endpoints[
    endpointName as keyof typeof emptySplitApi.endpoints
  ] as {
    select: (args: never) => (state: unknown) => { data?: unknown };
    useQuery: UseQuery<
      QDef<
        QueryArg & { offset?: number; limit?: number },
        { [key: number]: ResultType[]; lastFetchedPage: number }
      >
    >;
    useLazyQuery: UseLazyQuery<
      QDef<
        QueryArg & { offset?: number; limit?: number },
        { [key: number]: ResultType[]; lastFetchedPage: number }
      >
    >;
  };

  const {
    data: queryData,
    refetch,
    isFetching,
    isSuccess,
    isError,
    error,
    isUninitialized,
    isLoading: isInitialLoading,
    currentData
  } = useQuery(
    {
      ...currentParams,
      offset,
      limit
    },
    {
      ...currentOptions,
      // Always use the fresh skip value — currentOptions lags by 1 render
      // due to the useEffect sync, which can cause unwanted fetches.
      ...(options?.skip !== undefined && { skip: options.skip }),
      signal: abortControllerRef.current?.signal
    }
  );
  const pages = showCurrentData ? currentData : queryData;

  const [pageDataFetch] = useLazyQuery();

  const abortRequests = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
  }, []);

  // Each page is cached separately, and a tag invalidation refetches only the page at the
  // current offset, so a row that moved onto an earlier page can still sit in a stale later
  // one. With uniqueById it is shown once, at its earliest (freshest) position.
  const { data, pageMap } = useMemo(() => {
    const data: ResultType[] = [];
    const pageMap = {} as { [dataId: number]: number };
    for (let i = 0; i < (pages?.lastFetchedPage || 0) + 1; i++) {
      for (const item of pages?.[i] || []) {
        const id = (item as ResultType & { id?: number }).id;
        const seen = id != null && id in pageMap;
        if (uniqueById && seen) continue;
        // The earliest page: updatePaginatedData refreshes from there onwards.
        if (id != null && !seen) pageMap[id] = i;
        data.push(item);
      }
    }
    return { data, pageMap };
  }, [pages, uniqueById]);

  const isLoading = useMemo(
    () =>
      Boolean(
        !options?.skip && (isFetching || isUninitialized || isDataPending)
      ),
    [isDataPending, isFetching, isUninitialized, options?.skip]
  );
  const hasBeenInitiallyLoaded = useMemo(
    () => Boolean(!isUninitialized && !isInitialLoading),
    [isInitialLoading, isUninitialized]
  );
  // A reset starts again from page 0. Drop the cached later pages for these params, or
  // they are appended to the fresh page 0 (merge keeps every page it has) and repeat rows
  // that have since moved onto it. updateQueryData patches the entry without going through
  // merge. It must only run on an entry that already holds pages: on an entry whose first
  // fetch is still pending it skips the recipe but still applies an empty patch, which
  // writes `data: undefined` into the entry, and the next call then runs the recipe on
  // undefined. Object.keys(undefined) crashed every list on load (DEV-3432, 2026-10-15),
  // because the params/options effect below resets twice on the same key.
  const dropCachedLaterPages = useCallback(
    (args: QueryArg) => {
      const updateQueryData = emptySplitApi.util?.updateQueryData;
      if (!updateQueryData) return;
      const pageZeroArgs = { ...args, offset: 0, limit } as never;
      dispatch((_, getState) => {
        const { data } = select(pageZeroArgs)(getState());
        if (!data) return;
        dispatch(
          updateQueryData(endpointName as never, pageZeroArgs, ((draft: {
            [key: string]: unknown;
            lastFetchedPage: number;
          }) => {
            Object.keys(draft).forEach((key) => {
              if (key !== "lastFetchedPage" && Number(key) > 0) {
                delete draft[key];
              }
            });
            draft.lastFetchedPage = 0;
          }) as never) as never
        );
      });
    },
    [dispatch, endpointName, limit, select]
  );

  const clearData = useCallback(() => {
    dropCachedLaterPages(currentParamsRef.current);
    setOffset(0);
    setHasMoreData(true);
    setNewOffset(undefined);
    setIsDataPending(true);
  }, [dropCachedLaterPages]);

  const fetchMore = useCallback(() => {
    if (isLoading) return;
    // The page at the current offset failed (ENG-2449). newOffset still points at it, so the
    // branch below would not run, and advancing would skip it: ask for the same page again.
    if (isError) {
      safeRefetch(refetch, isUninitialized);
      return;
    }
    if (hasMoreData && newOffset === undefined) {
      setNewOffset(offset + limit);
    }
  }, [
    isLoading,
    isError,
    refetch,
    isUninitialized,
    offset,
    limit,
    newOffset,
    hasMoreData
  ]);

  // Refresh the row's page AND every loaded page after it. An edit can move rows across
  // page boundaries (a status change drops a row out of a filtered list, so the next page's
  // first rows slide up). Refreshing only the row's page left the later pages stale: the
  // moved rows showed twice and the rows behind them went missing until a reload. An id not
  // in the loaded data (e.g. not yet loaded) refreshes everything loaded.
  const updatePaginatedData = useCallback(
    async (dataId: number) => {
      const firstPage = pageMap[dataId] ?? 0;
      const lastPage = Math.max(pages?.lastFetchedPage ?? 0, firstPage);
      for (let page = firstPage; page <= lastPage; page++) {
        await pageDataFetch({ ...currentParams, offset: page * limit, limit })
          .unwrap()
          .catch(() => {});
      }
    },
    [pageMap, pages?.lastFetchedPage, currentParams, pageDataFetch, limit]
  );

  const refetchAllPages = useCallback(async () => {
    if (options?.skip) return;
    const lastPage = pages?.lastFetchedPage ?? 0;
    let lastPageRows: unknown[] | undefined;
    for (let page = 0; page <= lastPage; page++) {
      const response = await pageDataFetch({
        ...currentParams,
        offset: page * limit,
        limit
      })
        .unwrap()
        .catch(() => undefined);
      lastPageRows = response?.[page];
    }
    // The pages may have grown (rows moved into this list since they loaded), and the hasMoreData effect can
    // only ever turn it off: re-arm it from the last page, or a full page would be the end of the list.
    if (Array.isArray(lastPageRows)) {
      setHasMoreData(lastPageRows.length >= limit);
    }
  }, [
    options?.skip,
    pages?.lastFetchedPage,
    currentParams,
    pageDataFetch,
    limit
  ]);

  useEffect(() => {
    if (newOffset !== undefined && newOffset !== offset) {
      const updateFn = () => {
        if (newOffset === 0) {
          clearData();
        } else {
          setOffset(newOffset);
        }
      };
      if (withTransition) {
        requestAnimationFrame(() => {
          updateFn();
        });
      } else {
        updateFn();
      }
    }
  }, [offset, newOffset, clearData, withTransition]);

  useEffect(() => {
    if (!isEqual(params, currentParams) || !isEqual(options, currentOptions)) {
      // When skip is active, freeze internal params/options so useQuery never
      // sees new query args while it should be dormant.
      if (options?.skip) return;
      // Only abort the in-flight request when params/options actually
      // changed in value — a new object *reference* with identical content
      // (e.g. a caller rebuilding its params object inline every render)
      // must not cancel a request that's still valid.
      abortRequests();
      clearData();
      // The entry about to be shown may hold pages from an earlier visit to these params.
      dropCachedLaterPages(params);
      currentParamsRef.current = params;
      setCurrentParams(params);
      setCurrentOptions(options);
    }
  }, [
    params,
    currentParams,
    clearData,
    dropCachedLaterPages,
    options,
    currentOptions,
    abortRequests
  ]);

  useEffect(() => {
    if (
      !hasMoreData ||
      isLoading ||
      !pages ||
      !pages[pages?.lastFetchedPage || 0]
    )
      return;

    if (pages[pages?.lastFetchedPage || 0].length < limit) {
      setHasMoreData(false);
    } else {
      setHasMoreData(true);
    }
  }, [pages, limit, isLoading, hasMoreData, offset, clearData]);

  useEffect(() => {
    if (
      newOffset === offset &&
      isSuccess &&
      pages?.lastFetchedPage === Math.abs(offset / limit)
    ) {
      setNewOffset(undefined);
    }
  }, [
    setNewOffset,
    newOffset,
    isSuccess,
    offset,
    limit,
    pages?.lastFetchedPage
  ]);

  useEffect(() => {
    setIsDataPending(false);
  }, [data]);

  const refetchData = useCallback(() => {
    clearData();
    safeRefetch(refetch, isUninitialized);
  }, [refetch, clearData, isUninitialized]);

  return {
    refetch: refetchData,
    isFetching,
    data,
    isLoading,
    isSuccess,
    isError,
    error,
    isUninitialized,
    hasBeenInitiallyLoaded,
    hasMoreData,
    fetchMore,
    clearData,
    offset,
    isDataPending,
    updatePaginatedData,
    refetchAllPages,
    lastFetchedPage: pages?.lastFetchedPage ?? 0,
    abortController: abortControllerRef.current,
    abortRequests
  };
}
